import { GetProductRecommendationsSettingsQuery } from '../types';

export type ProductRecsKind =
  | 'SCRIPT'
  | 'STATIC_MANAGED'
  | 'STATIC_JSON'
  | 'DYNAMIC_JSON'
  | 'ML_ENGINE'
  | 'CERTONA'
  | 'NONE';

type ProductRecommendations = NonNullable<
  GetProductRecommendationsSettingsQuery['retailerSettings']['productRecommendations']
>;

const isManagedRecsUrl = (urlTemplate: string): boolean =>
  urlTemplate.includes('product-recommendations');

const isStaticJsonUrl = (urlTemplate: string): boolean => {
  try {
    const { pathname, host } = new URL(urlTemplate);
    return (
      /\.json(\?|#|$)/i.test(pathname) ||
      /(^|\.)assets\.narvar\.com$/i.test(host)
    );
  } catch {
    return /\.json(\?|#|$)/i.test(urlTemplate);
  }
};

const classifyJsonUrl = (urlTemplate: string | undefined): ProductRecsKind => {
  if (!urlTemplate) return 'DYNAMIC_JSON';
  if (isManagedRecsUrl(urlTemplate)) return 'STATIC_MANAGED';
  if (isStaticJsonUrl(urlTemplate)) return 'STATIC_JSON';
  return 'DYNAMIC_JSON';
};

const urlTemplateFor = (
  productRecommendations: ProductRecommendations,
): string | undefined => {
  switch (productRecommendations.type) {
    case 'JSON':
      return productRecommendations.api?.urlTemplate;
    case 'ML_ENGINE':
      return productRecommendations.jsonApi?.urlTemplate;
    case 'CERTONA':
      return productRecommendations.urlTemplate;
    default:
      return undefined;
  }
};

export const productRecsUrlHost = (
  productRecommendations: ProductRecommendations | null | undefined,
): string | undefined => {
  if (!productRecommendations) return undefined;
  const urlTemplate = urlTemplateFor(productRecommendations);
  if (!urlTemplate) return undefined;
  try {
    return new URL(urlTemplate).host;
  } catch {
    return undefined;
  }
};

const resolveProductRecsKind = (
  productRecommendations: ProductRecommendations | null | undefined,
  mlProductRecsEnabled: boolean | undefined,
): ProductRecsKind => {
  const pr = productRecommendations;
  if (!pr) return 'NONE';

  switch (pr.type) {
    case 'SCRIPT':
      return 'SCRIPT';
    case 'CERTONA':
      return 'CERTONA';
    case 'JSON':
      return classifyJsonUrl(pr.api?.urlTemplate);
    case 'ML_ENGINE':
      if (mlProductRecsEnabled === undefined) return 'NONE';
      if (mlProductRecsEnabled) return 'ML_ENGINE';
      return classifyJsonUrl(pr.jsonApi?.urlTemplate);
    default:
      return 'NONE';
  }
};

export default resolveProductRecsKind;
